// Dynamic lighting: the always-night city, lit. A small light map (a RenderTexture, mapScale of the view) is cleared to black and
// every light near the camera is drawn into it as a pooled additive radial sprite. A filter on the world layer (not the HUD) then
//   lit = scene * (ambient + light * gain) + light * wash        (lights brighten and tint, ambient darkens)
// and a real bloom (bright-pass of the lit picture, blurred at a fraction of the resolution) is added on top.
// Everything is pooled and culled to the view; the only textures (one radial gradient) are made once. Tuning is data:
// common/data/city-art.json `lighting`. Off with `?light=0` or Settings > "Neon lighting"; Low effects turns the bloom off.
import { BlurFilter, ColorMatrixFilter, Container, Filter, GlProgram, Rectangle, RenderTexture, Sprite, Texture, defaultFilterVert, type FilterSystem, type RenderSurface } from 'pixi.js';
import { CITY_ART } from '../../common/src/data';
import { hash2 } from '../../common/src/util';
import { canvas, tex } from './art';
import type { Renderer } from './render';
import type { HdLight } from './hd-scene';
import { COLOR } from './theme';
import { flashStrength } from './fx-pool';

const LT = CITY_ART.lighting;
const hex = (c: string) => parseInt(c.slice(1), 16);
type FlashKind = keyof typeof LT.flash;

/** `?light=0` in the URL turns the whole pass off (nothing is created); anything else leaves it to the setting.
 *  Browser tests (navigator.webdriver) default to off like the 2x art (render.ts HD): CI renders in software GL and the
 *  filter + bloom slowed timing-sensitive tests. `?light=1` turns it back on for screenshot scripts. */
const LIGHT_Q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('light') : null;
export const LIGHT_PARAM_OFF = LIGHT_Q === '0' || (LIGHT_Q !== '1' && typeof navigator !== 'undefined' && !!navigator.webdriver);

/** soft radial light: bright core, smooth falloff (generated once) */
let lightTex: Texture | undefined;
function radialLight(): Texture {
  if (lightTex) return lightTex;
  const [c, g] = canvas(64, 64), grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  for (const [o, a] of [[0, 1], [0.2, 0.72], [0.45, 0.32], [0.75, 0.08], [1, 0]]) grd.addColorStop(o, `rgba(255,255,255,${a})`);
  g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
  lightTex = tex(c); lightTex.source.scaleMode = 'linear'; return lightTex;
}


const FRAG_COMMON = `
in vec2 vTextureCoord; out vec4 finalColor;
uniform sampler2D uTexture; uniform sampler2D uLight;
uniform highp vec4 uInputSize; uniform highp vec4 uOutputFrame;
uniform vec3 uAmb; uniform float uGain; uniform float uWash;
vec3 litScene(vec2 q, vec2 uv) {
  vec3 sc = texture(uTexture, uv).rgb, L = texture(uLight, q).rgb;
  float lum = dot(sc, vec3(0.3, 0.59, 0.11));
  // light tints what it falls on (gain) and adds its own colour, a little more over lit surfaces
  return sc * (uAmb + L * uGain) + L * uWash * (0.35 + lum);
}`;
const FRAG_LIT = FRAG_COMMON + `
uniform sampler2D uBloom; uniform float uBloomK;
void main() {
  vec2 q = vTextureCoord / (uOutputFrame.zw * uInputSize.zw);
  vec3 c = litScene(q, vTextureCoord);
  if (uBloomK > 0.0) c += texture(uBloom, q).rgb * uBloomK;
  // highlight roll-off: above the knee the brightest channel eases toward 1 instead of clipping, and the whole colour
  // is scaled with it, so hot neon keeps its hue (pink stays pink) instead of burning out to white
  float m = max(c.r, max(c.g, c.b)), k = 0.72;
  if (m > k) c *= (k + (1.0 - k) * (1.0 - exp(-(m - k) / (1.0 - k)))) / m;
  finalColor = vec4(c, 1.0);
}`;
const FRAG_BRIGHT = FRAG_COMMON + `
uniform float uThresh; uniform vec2 uSceneUv;
void main() {
  vec2 q = vTextureCoord / (uOutputFrame.zw * uInputSize.zw);
  vec3 c = litScene(q, q * uSceneUv); // this pass renders to a smaller target, so vTextureCoord only spans part of the input
  float m = max(c.r, max(c.g, c.b));
  finalColor = vec4(c * smoothstep(uThresh, uThresh + 0.35, m), 1.0);
}`;

/** The composite filter: ambient + lights over the scene, plus bloom. apply() makes the bloom texture first (bright-pass into
 *  a small target, blur it there) and then runs the composite; the sprites themselves are never blurred. */
class LightFilter extends Filter {
  bloomOn = true; bloomMul = 1; private a?: RenderTexture; private b?: RenderTexture;
  private bright: Filter; private blur: BlurFilter; private u: Record<string, number | number[]>;
  constructor(lightSrc: Texture['source']) {
    const u = {
      uAmb: { value: [0.3, 0.3, 0.4], type: 'vec3<f32>' }, uGain: { value: LT.gain, type: 'f32' }, uWash: { value: LT.wash, type: 'f32' },
      uBloomK: { value: LT.bloom.strength, type: 'f32' }, uSceneUv: { value: [1, 1], type: 'vec2<f32>' }, uThresh: { value: LT.bloom.threshold, type: 'f32' },
    };
    const mk = (fragment: string, name: string, extra: object) => ({ glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment, name }), resources: { lightU: u, uLight: lightSrc, ...extra } });
    super(mk(FRAG_LIT, 'nk-light', { uBloom: Texture.EMPTY.source }));
    // Pixi wraps `lightU` in its own UniformGroup: write through that, not the plain object passed in
    this.u = (this.resources.lightU as unknown as { uniforms: Record<string, number | number[]> }).uniforms;
    this.bright = new Filter(mk(FRAG_BRIGHT, 'nk-bright', {}));
    this.blur = new BlurFilter({ strength: LT.bloom.blur, quality: 3 });
    this.blur.resolution = 1;
  }
  setWash(k: number) { this.u.uWash = LT.wash * k; }
  setAmbient(c: number) { this.u.uAmb = [((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255]; }
  apply(fm: FilterSystem, input: Texture, output: RenderSurface, clear: boolean) {
    if (!this.bloomOn) { this.u.uBloomK = 0; super.apply(fm, input, output, clear); return; }
    this.u.uBloomK = LT.bloom.strength * this.bloomMul;
    // own targets of exactly the bloom size (the texture pool rounds up to powers of two, which would shift the uvs)
    const w = Math.max(8, Math.ceil(input.frame.width * LT.bloom.scale)), h = Math.max(8, Math.ceil(input.frame.height * LT.bloom.scale));
    if (!this.a) { this.a = RenderTexture.create({ width: w, height: h, resolution: 1 }); this.b = RenderTexture.create({ width: w, height: h, resolution: 1 }); }
    else if (this.a.width !== w || this.a.height !== h) { this.a.resize(w, h); this.b!.resize(w, h); }
    this.u.uSceneUv = [input.frame.width / input.source.width, input.frame.height / input.source.height];
    this.bright.apply(fm, input, this.a, true);
    this.blur.apply(fm, this.a, this.b!, true);
    this.resources.uBloom = this.b!.source;
    super.apply(fm, input, output, clear);
  }
}

interface Spot { x: number; y: number; w: number; h: number; col: number; k: number; flick: boolean; ph: number }
interface Flash { x: number; y: number; kind: FlashKind; at: number; weaponScale?: number; shown?: boolean; hack?: { col: number; scale: number; flicker: boolean } }

export class Lighting {
  on = true;
  private rt: RenderTexture; private filter: LightFilter;
  private lit = new Container(); private grade = new ColorMatrixFilter(); private lv = LT.comfort.levels[0];
  private pool: Sprite[] = []; private n = 0;
  private spots: Spot[] = []; private flashes: Flash[] = [];
  private ambient = hex(LT.ambient); private w = 0; private h = 0; private area = new Rectangle();

  constructor(private r: Renderer) {
    this.rt = RenderTexture.create({ width: 8, height: 8, resolution: 1 });
    this.rt.source.style.scaleMode = 'linear'; // soft light edges; the sprites themselves stay nearest-neighbour
    this.filter = new LightFilter(this.rt.source);
    this.filter.setAmbient(this.ambient);
    this.filter.resolution = 'inherit'; // filters default to 1x: the 2x art would be drawn at half detail and stretched (fuzzy)
    r.world.filters = [this.filter]; // only the world: the HUD (r.screen and the DOM) is never lit
    this.grade.resolution = 'inherit';
    this.setLevel(0);
  }

  /** Settings > Neon intensity: 0 Full, 1 Soft, 2 Low (city-art.json lighting.comfort). Only the scenery steps back: a colour
   *  matrix on the ground layer (buildings, streets), the neon glow and wet decals; actors, vehicles, pickups and gameplay
   *  lights are never graded. */
  setLevel(i: number) {
    const L = LT.comfort.levels, c = L[Math.max(0, Math.min(L.length - 1, i | 0))]; this.lv = c;
    const s = c.sat, v = c.val, a = (1 - s) * v;
    // out = v * (lum*(1-s) + s*rgb), per channel (luma weights as in the shader)
    this.grade.matrix = [v * s + a * 0.3, a * 0.59, a * 0.11, 0, 0, a * 0.3, v * s + a * 0.59, a * 0.11, 0, 0, a * 0.3, a * 0.59, v * s + a * 0.11, 0, 0, 0, 0, 0, 1, 0];
    this.filter.bloomMul = c.bloom; this.filter.setWash(c.wash);
    this.r.neon.alpha = c.glow; this.r.decals.alpha = c.reflect;
    this.applyGrade();
  }
  private applyGrade() { this.r.ground.filters = this.on && (this.lv.sat < 1 || this.lv.val < 1) ? [this.grade] : []; }

  private zoneAmb = -1;
  /** the night colour at a world point: the base ambient, eased toward a lighting zone's own (city-art.json
   *  lighting.zones, e.g. the monastery) over `fade` tiles inside its edge; steps of 1/32 so the uniform rarely changes */
  private zoneAmbient(x: number, y: number) {
    let c = this.ambient;
    for (const z of LT.zones) {
      const T = 16, d = Math.min(x - z.x * T, (z.x + z.w) * T - x, y - z.y * T, (z.y + z.h) * T - y);
      if (d <= 0) continue;
      const k = Math.round(Math.min(1, d / (z.fade * T)) * 32) / 32, to = hex(z.ambient);
      const ch = (s: number) => Math.round(((c >> s) & 255) + (((to >> s) & 255) - ((c >> s) & 255)) * k);
      c = (ch(16) << 16) | (ch(8) << 8) | ch(0);
    }
    return c;
  }

  /** the night colour at a world point (base ambient, or a lighting zone's), for overlays drawn above the filter */
  ambientAt(x: number, y: number) { return this.zoneAmbient(x, y); }

  setOn(on: boolean) { this.on = on; this.r.world.filters = on ? [this.filter] : []; this.applyGrade(); }

  /** painted-scene lights that are not live signs (street lamps, vending machines, window and pool glows) */
  addScene(ls: HdLight[]) {
    ls.forEach((l, i) => {
      if (l.live || l.alpha < 0.3) return;
      const m = LT.lamp.radiusMul * (l.pool ? 1.1 : 1);
      this.spots.push({ x: l.x + l.w / 2, y: l.y + l.h / 2, w: (l.w + 14) * m, h: (l.h + 14) * m, col: hex(l.color), k: Math.min(1, l.alpha) * LT.lamp.intensity, flick: !l.pool && i % LT.lamp.flickerEvery === 0, ph: i * 1.7 });
    });
  }

  /** a short light from a game event (muzzle flash, explosion, hack, EMP ...) */
  flash(k: string, x: number, y: number) {
    if (!this.on || !(k in LT.flash)) return;
    if (this.flashes.length >= 24) this.flashes.shift();
    this.flashes.push({ x, y, kind: k as FlashKind, at: this.r.t });
  }
  /** Weapon-only pulse at the fitted bore, scaled by the caller's comfort grade. */
  weaponFlash(x: number, y: number, scale: number) {
    if (!this.on) return;
    if (this.flashes.length >= CITY_ART.weaponFx.shots) this.flashes.shift();
    this.flashes.push({ x: Math.round(x), y: Math.round(y), kind: 'shot', at: this.r.t, weaponScale: scale });
  }

  /** Accepted-hack light at the affected object: a short bounded flicker (never on the acknowledgement), scaled by the comfort grade.
   *  Cosmetic only; it does not change what any AI can see. */
  hackFlash(x: number, y: number, hack: string, scale: number) {
    if (!this.on) return;
    const cap = CITY_ART.hackFx.lights;
    if (this.flashes.filter(f => f.hack).length >= cap) { const i = this.flashes.findIndex(f => f.hack); this.flashes.splice(i, 1); }
    const col = hack === 'short_circuit' ? hex(COLOR.text) : hex(COLOR.interactive);
    this.flashes.push({ x: Math.round(x), y: Math.round(y), kind: 'hack', at: this.r.t, hack: { col, scale, flicker: hack === 'short_circuit' } });
  }

  private put(pool: Sprite[], i: number, parent: Container) {
    let s = pool[i];
    if (!s) { s = new Sprite(radialLight()); s.anchor.set(0.5); s.blendMode = 'add'; s.eventMode = 'none'; parent.addChild(s); pool[i] = s; }
    s.visible = true; return s;
  }
  /** one light into the light map (the bloom is made from the lit picture, not drawn per light) */
  private light(x: number, y: number, w: number, h: number, col: number, a: number, _bloom = 0, rot = 0, ax = 0.5) {
    if (a <= 0.01 || this.n >= LT.maxLights) return;
    const s = this.put(this.pool, this.n++, this.lit); s.x = x; s.y = y; s.width = w; s.height = h; s.tint = col; s.alpha = a; s.rotation = rot; s.anchor.x = ax;
  }

  /** after Renderer.frame has placed the camera, world, vehicles and signals for this frame */
  update(lowFx: boolean, vw: number, vh: number, fix: number[], me?: { x: number; y: number }) {
    if (!this.on) return;
    const r = this.r, z = r.cam.zoom, t = r.t, ms = LT.mapScale;
    const w = Math.ceil(vw * ms), h = Math.ceil(vh * ms);
    if (w !== this.w || h !== this.h) { this.w = w; this.h = h; this.rt.resize(w, h); }
    this.filter.bloomOn = !lowFx;
    r.world.filterArea = this.area.set(-r.world.x / z, -r.world.y / z, vw / z, vh / z); // the screen, in the world's own space
    r.ground.filterArea = this.area; // the grade filter on the ground layer sees only the screen too (ground has no transform)
    const amb = this.zoneAmbient(r.cam.x, r.cam.y);
    if (amb !== this.zoneAmb) { this.zoneAmb = amb; this.filter.setAmbient(amb); }
    const lk = this.lv.lights;
    const m = 80, x0 = r.cam.x - vw / 2 / z - m, x1 = r.cam.x + vw / 2 / z + m, y0 = r.cam.y - vh / 2 / z - m, y1 = r.cam.y + vh / 2 / z + m;
    const vis = (x: number, y: number, rad: number) => x + rad > x0 && x - rad < x1 && y + rad > y0 && y - rad < y1;
    this.n = 0;
    // neon signs and façade glows: the renderer already flickers their alpha, so the light flickers with the neon
    for (const g of r.signGlows) {
      const s = g.g; if (!s.visible || s.blendMode !== 'add') continue;
      this.light(s.x, s.y, s.width * LT.sign.radiusMul, s.height * LT.sign.radiusMul, s.tint as number, Math.min(1, s.alpha * 1.6) * LT.sign.intensity * lk);
    }
    // painted lamps, vending machines, window glows
    for (const p of this.spots) {
      if (!vis(p.x, p.y, Math.max(p.w, p.h) / 2)) continue;
      const f = p.flick && !lowFx ? (hash2(Math.floor(t * 8), p.ph * 10) > 0.985 ? 0.3 : 0.88 + 0.12 * Math.sin(t * 3 + p.ph)) : 1;
      this.light(p.x, p.y, p.w, p.h, p.col, p.k * f * lk);
    }
    // traffic signals
    for (const L of r.signals) for (const s of L) if (vis(s.x, s.y, 20)) this.light(s.x, s.y, LT.signal.radius * 2, LT.signal.radius * 2, s.tint as number, s.alpha * LT.signal.intensity, LT.signal.bloom);
    // vehicles: headlights ahead, taillights behind, police sirens
    for (const e of r.pools.veh.values()) {
      const v = e.s; if (!vis(v.x, v.y, 120)) continue;
      if (e.head.visible) {
        this.light(e.head.x, e.head.y, LT.headlight.length * 2, LT.headlight.width, 0xfff0c0, LT.headlight.intensity, 0, e.head.rotation, 0);
        const hx = e.head.x - v.x, hy = e.head.y - v.y; // the nose is half a car length out: the tail is the mirror point
        this.light(v.x - hx, v.y - hy, LT.taillight.radius * 2, LT.taillight.radius * 2, 0xff2030, LT.taillight.intensity, LT.taillight.bloom);
      }
      if (e.siren.visible) this.light(e.siren.x, e.siren.y, LT.siren.radius * 2, LT.siren.radius * 2, e.siren.tint as number, LT.siren.intensity, LT.siren.bloom);
    }
    // everyone carries a faint light so they stay readable in the dark; the player a little more
    const A = LT.actor, ac = hex(A.color);
    for (const e of r.pools.chr.values()) { const p = e.rig?.root ?? e.s; if (vis(p.x, p.y, A.radius)) this.light(p.x, p.y, A.radius * 2, A.radius * 2, ac, A.intensity * 0.6); }
    if (me) this.light(me.x, me.y, A.radius * 2.6, A.radius * 2.6, ac, A.intensity);
    for (const p of r.pools.pk.values()) if (vis(p.x, p.y, 20)) this.light(p.x, p.y, 36, 36, 0xfff0a0, 0.4);
    // short-circuit hazards
    fix.forEach((tm, i) => {
      const f = r.fixG[i]; if (!(tm > 0) || !vis(f.x, f.y, 60)) return;
      const k = 0.7 + 0.3 * Math.sin(t * 40 + i);
      this.light(f.x, f.y, LT.hazard.radius * 2, LT.hazard.radius * 2, k > 0.85 ? 0xc8f0ff : 0x40a0ff, LT.hazard.intensity * k, LT.hazard.bloom);
    });
    // event flashes
    const HFX = CITY_ART.hackFx;
    this.flashes = this.flashes.filter(f => t - f.at < (f.hack ? HFX.lightSec : f.weaponScale === undefined ? LT.flash[f.kind].sec : CITY_ART.weaponFx.eventGraceSec));
    for (const f of this.flashes) {
      const d = LT.flash[f.kind], u = 1 - (t - f.at) / d.sec;
      if (f.hack) { // three steps at most, never brighter than one light of the comfort scale
        const age = t - f.at, k = (f.hack.flicker ? (Math.floor(age / 0.03) % 2 === 0 ? 1 : 0.3) : 1) * (1 - age / HFX.lightSec);
        if (vis(f.x, f.y, 48)) this.light(f.x, f.y, 88, 88, f.hack.col, 0.8 * f.hack.scale * k);
        continue;
      }
      if (f.weaponScale !== undefined) {
        const scale = f.weaponScale, fade = flashStrength(f, t, CITY_ART.weaponFx.flashSec, CITY_ART.weaponFx.eventGraceSec);
        if (vis(f.x, f.y, d.radius)) this.light(f.x, f.y, d.radius * scale, d.radius * scale, hex(COLOR.world), .7 * scale * fade);
        continue;
      }
      if (vis(f.x, f.y, d.radius)) this.light(f.x, f.y, d.radius * 2 * (0.7 + 0.3 * u), d.radius * 2 * (0.7 + 0.3 * u), hex(d.color), d.intensity * u, d.bloom);
    }
    for (let i = this.n; i < this.pool.length; i++) this.pool[i].visible = false;
    // draw the light map in the world's transform, scaled to the map
    this.lit.scale.set(z * ms); this.lit.position.set(r.world.x * ms, r.world.y * ms);
    r.app.renderer.render({ container: this.lit, target: this.rt, clear: true, clearColor: 0x000000 });
  }
}
